import { ComparisonDates, PropertyData } from 'types/properties-data';
import { reportException } from 'core/exceptions';
import { useGetPlacesForTagWithCompareQuery } from 'hooks/queries/tags/use-get-places-for-tag-with-compare-query';
import {
    RANKING_SALES_FILTERED_OUT_METRICS,
    RETAIL_SALES_API_FILTERED_OUT_METRICS,
} from 'shared/constants/retail-sales';
import { useGetAccountPropertiesQuery } from 'hooks/queries/properties/use-get-account-properties-query';
import { useGetSalesMetricsByTagIdQuery } from 'hooks/queries/properties/use-get-sales-metrics-by-tag-id-query';
import {
    getCombinedPropertiesMetricsContent,
    getContentWithFilteredMetrics,
    mergeVisitsFromTagsAPIResponse,
    normalizePropertiesResponse,
} from './property-utils';
import { useRetailSalesPermissions } from 'hooks/use-retail-sales-permissions/use-retail-sales-permissions';
import { useMyZonePortfolioPermissions } from 'features/my-zone/portfolio/hooks/use-my-zone-portfolio-permissions';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    accountPropertiesQueryKeys,
    salesMetricsQueryKeys,
} from 'hooks/queries/properties/property-keys';
import { placesWithTagsQueryKeys } from 'hooks/queries/tags/tag-keys';
import { useMyTagComparisonDates } from 'features/my-zone/shared/hooks/hooks';
import { useDefaultComparisonDates } from 'hooks/use-default-comparison-dates/use-default-comparison-dates';

type AllPropertiesValuesType = {
    id: string;
    dates?: ComparisonDates | undefined;
    combineTagsData?: boolean;
    enabled?: boolean;
};

type AllPropertiesValuesFunctionType = Omit<
    AllPropertiesValuesType,
    'id' | 'dates' | 'combineTagsData'
> & {
    hasSalesPermission: boolean;
    hasSalesInMainApiPermission: boolean;
    hasShareMetricsPermission: boolean;
    salesMetricsByTagId: any | undefined;
    accountProperties: any;
    placesForTagData?: any | undefined;
};

export const useResetAllPropertiesValues = () => {
    const queryClient = useQueryClient();

    return useCallback(
        async (tagId: string) => {
            await Promise.all([
                queryClient.resetQueries({
                    queryKey: salesMetricsQueryKeys.byTagId({ tagId }),
                }),
                queryClient.resetQueries({
                    queryKey: accountPropertiesQueryKeys.byTagId({ tagId }),
                }),
                queryClient.resetQueries({
                    queryKey: placesWithTagsQueryKeys.byTagId({ tagId }),
                }),
            ]);
        },
        [queryClient],
    );
};

export const useGetAllPropertiesValues = ({
    enabled = true,
    dates,
    id,
    combineTagsData,
}: AllPropertiesValuesType) => {
    const { hasRetailSalesPermission, hasTenantsSalesRankingMetric } = useRetailSalesPermissions();
    const { hasEnabledShareMetrics } = useMyZonePortfolioPermissions();
    const tagsDates = useMyTagComparisonDates();
    const defaultDates = useDefaultComparisonDates();
    const refetchTagProperties = useResetAllPropertiesValues();

    const currentDates = dates ?? tagsDates ?? defaultDates;

    const isEnabledGetSalesMetricsByTagId = enabled && hasRetailSalesPermission;
    const isEnabledGetPlacesForTagWithCompare = enabled && !!combineTagsData;
    const isEnabledGetAccountProperties = enabled;

    const {
        data: accountProperties,
        isError: isErrorGetAccountProperties,
        isFetching: isFetchingGetAccountProperties,
        isFetched: isFetchedGetAccountProperties,
    } = useGetAccountPropertiesQuery({
        accountPropertiesTagId: id,
        dates: currentDates,
        enabled: isEnabledGetAccountProperties,
    });

    const {
        data: salesMetricsByTagId,
        isError: isErrorGetSalesMetricsByTagId,
        isFetching: isFetchingGetSalesMetricsByTagId,
        isFetched: isFetchedGetSalesMetricsByTagId,
    } = useGetSalesMetricsByTagIdQuery({
        accountPropertiesTagId: id,
        dates: currentDates,
        enabled: isEnabledGetSalesMetricsByTagId,
    });

    const {
        data: placesForTagData,
        isFetching: isFetchingGetPlacesForTag,
        isError: isErrorGetPlacesForTag,
        isFetched: isFetchedGetPlacesForTagWithCompare,
        error,
    } = useGetPlacesForTagWithCompareQuery({
        tagId: id,
        dates: currentDates,
        enabled: isEnabledGetPlacesForTagWithCompare,
    });

    const shouldFetchedGetPlacesForTagWithCompare = isEnabledGetPlacesForTagWithCompare
        ? isFetchedGetPlacesForTagWithCompare
        : true;
    const shouldFetchedGetAccountProperties = isEnabledGetAccountProperties
        ? isFetchedGetAccountProperties
        : true;
    const shouldFetchedGetSalesMetricsByTagId = isEnabledGetSalesMetricsByTagId
        ? isFetchedGetSalesMetricsByTagId
        : true;

    const isFetched =
        shouldFetchedGetPlacesForTagWithCompare &&
        shouldFetchedGetAccountProperties &&
        shouldFetchedGetSalesMetricsByTagId;

    const isLoading =
        isFetchingGetPlacesForTag ||
        isFetchingGetSalesMetricsByTagId ||
        isFetchingGetAccountProperties;

    // handle data
    const isError =
        isErrorGetAccountProperties || isErrorGetSalesMetricsByTagId || isErrorGetPlacesForTag;

    if (isError) {
        // TODO - update message
        reportException(error, {
            payload: {
                id,
                dates: currentDates,
                hasSalesPermission: hasRetailSalesPermission,
                hasSalesInMainApiPermission: hasTenantsSalesRankingMetric,
                hasShareMetricsPermission: hasEnabledShareMetrics,
            },
            message: '',
        });

        throw error;
    }

    const getAllPropertiesValuesFn = useCallback(
        () =>
            getAllPropertiesValues({
                placesForTagData: isEnabledGetPlacesForTagWithCompare
                    ? placesForTagData
                    : undefined,
                salesMetricsByTagId: hasRetailSalesPermission ? salesMetricsByTagId : undefined,
                accountProperties,
                hasSalesPermission: !!hasRetailSalesPermission,
                hasSalesInMainApiPermission: !!hasTenantsSalesRankingMetric,
                hasShareMetricsPermission: hasEnabledShareMetrics,
            }),
        [
            accountProperties,
            hasEnabledShareMetrics,
            hasRetailSalesPermission,
            hasTenantsSalesRankingMetric,
            isEnabledGetPlacesForTagWithCompare,
            placesForTagData,
            salesMetricsByTagId,
        ],
    );

    const refetch = useCallback(
        async (overrideTagId?: string) => {
            const tagId = overrideTagId ?? id;

            await refetchTagProperties(tagId);
        },
        [id, refetchTagProperties],
    );

    const data = useMemo(() => getAllPropertiesValuesFn(), [getAllPropertiesValuesFn]);

    return {
        isLoading,
        isError,
        isFetched,
        data,
        refetch,
    };
};

const getAllPropertiesValues = ({
    hasSalesInMainApiPermission = false,
    salesMetricsByTagId,
    placesForTagData,
    accountProperties,
    hasShareMetricsPermission = false,
}: AllPropertiesValuesFunctionType): PropertyData[] => {
    // Original API doesn't have visits for chains
    if (accountProperties && placesForTagData) {
        mergeVisitsFromTagsAPIResponse(accountProperties, placesForTagData);
    }

    const mainFilteredContent = hasSalesInMainApiPermission
        ? accountProperties
        : getContentWithFilteredMetrics({
              content: accountProperties,
              filteredList: RANKING_SALES_FILTERED_OUT_METRICS,
          });

    const salesFilteredContent = hasSalesInMainApiPermission
        ? getContentWithFilteredMetrics({
              content: salesMetricsByTagId,
              filteredList: RETAIL_SALES_API_FILTERED_OUT_METRICS,
          })
        : salesMetricsByTagId;

    return normalizePropertiesResponse(
        getCombinedPropertiesMetricsContent(mainFilteredContent, salesFilteredContent),
        hasShareMetricsPermission,
    );
};
